import type { StoreApi } from "zustand";
import type { TPaginationKey, TLeadData } from "../chat-domain.types";
import type { TChatStore } from "../chat-store.types";
import type { TMessage } from "./message.types";
import {
  messageListInitialState,
  MAX_MESSAGES_ON_SCREEN,
} from "./message.initial-state";

type TSetState = StoreApi<TChatStore>["setState"];

export const createMessageListActions = (set: TSetState) => ({
  setMessageListLoading: (chatId: string) =>
    set(() => ({
      messageList: { ...messageListInitialState, loading: true, chatId },
    })),

  setMessageListFulfilled: ({
    chatId,
    messages,
    hasOlderMessagesToFetch,
    actualLead,
  }: {
    chatId: string;
    messages: TMessage[];
    hasOlderMessagesToFetch: TPaginationKey | boolean;
    actualLead?: TLeadData;
  }) =>
    set((state) => {
      if (state.messageList.chatId !== chatId) return {};

      const { start } = state.messageList.pagination;
      const source = [...state.messageList.source, ...messages];
      const newStart = start ? start + MAX_MESSAGES_ON_SCREEN : 0;
      const newEnd = newStart + MAX_MESSAGES_ON_SCREEN;
      const prevFetchKey =
        typeof state.messageList.pagination.hasOlderMessagesToFetch === "object"
          ? state.messageList.pagination.hasOlderMessagesToFetch?.item
          : undefined;
      const nextFetchKey =
        typeof hasOlderMessagesToFetch === "object"
          ? hasOlderMessagesToFetch?.item
          : undefined;

      return {
        messageList: {
          ...messageListInitialState,
          fetched: true,
          pagination: {
            start: newStart,
            end: newEnd,
            hasOlderMessages: newEnd < source.length,
            hasOlderMessagesToFetch,
            fetchingOlderMessages: false,
            loadedOlderMessages:
              newStart >= MAX_MESSAGES_ON_SCREEN &&
              prevFetchKey !== nextFetchKey,
          },
          source,
          chatId,
          actualLead,
        },
      };
    }),

  loadOlderMessages: () =>
    set((state) => {
      const { start, end, hasOlderMessagesToFetch } =
        state.messageList.pagination;
      const newStart = start + MAX_MESSAGES_ON_SCREEN;
      const newEnd = end + MAX_MESSAGES_ON_SCREEN;
      return {
        messageList: {
          ...state.messageList,
          pagination: {
            start: newStart,
            end: newEnd,
            hasOlderMessages: newEnd < state.messageList.source.length,
            hasOlderMessagesToFetch,
            fetchingOlderMessages: true,
            loadedOlderMessages: true,
          },
        },
      };
    }),

  addMyMessage: (messages: TMessage[]) =>
    set((state) => ({
      messageList: {
        ...state.messageList,
        newMessages: [...state.messageList.newMessages, ...messages],
      },
    })),

  setMessageListRejected: (source: TMessage[]) =>
    set(() => ({
      messageList: { ...messageListInitialState, error: true, source },
    })),

  resetMessageList: () =>
    set(() => ({ messageList: { ...messageListInitialState } })),
});
